/**
 * SelectionSummary.jsx
 *
 * Inline prose summary of the current wizard selections. Used inside
 * StepSummary (and reusable elsewhere) to print a one-line read-back of
 * the user's choices so they can sanity-check before viewing results.
 *
 * PORTED from TH ToolFinder v4:
 *   tophammer-toolfinder-v4/src/components/ui/SelectionSummary.jsx
 *   (57 lines). The TF v4 version reads from `selections.drillingType`
 *   and APPLICATIONS / DRILLING_TYPES; MRT renames the field to
 *   `selections.drillingMethod` and the constant to DRILLING_METHODS to
 *   match its extended mining-method taxonomy.
 *
 * Skips parts whose source value is null — so a partial state still
 * produces a clean line ("You have selected: Underground drilling and
 * Top Hammer") without dangling "undefined" placeholders.
 *
 * @module components/ui/SelectionSummary
 * @since 2026-05-17 (Group D Sub-Plan 2, Task D3f)
 */

import { APPLICATIONS, DRILLING_METHODS } from '@data/wizardData';

export default function SelectionSummary({ selections, className = '' }) {
  if (!selections) return null;

  const parts = [];

  if (selections.application) {
    const app = APPLICATIONS.find((a) => a.id === selections.application);
    if (app) {
      parts.push(
        <span key="app" className="underline">
          {app.label}
        </span>
      );
    }
  }

  if (selections.drillingMethod) {
    const method = DRILLING_METHODS.find((m) => m.id === selections.drillingMethod);
    if (method) {
      if (parts.length > 0) parts.push(<span key="dm-sep"> and </span>);
      parts.push(
        <span key="dm" className="underline">
          {method.label}
        </span>
      );
    }
  }

  if (selections.threadSystem) {
    parts.push(
      <span key="th"> ({selections.threadSystem} thread)</span>
    );
  } else if (selections.shankType) {
    // DTH analogue — surface shank type with the same wrapper.
    parts.push(
      <span key="sh"> ({selections.shankType} shank)</span>
    );
  }

  if (selections.holeDiameter != null) {
    parts.push(<span key="dia-sep"> </span>);
    parts.push(
      <span key="dia" className="underline">
        {selections.holeDiameter}mm
      </span>
    );
    parts.push(<span key="dia-txt"> hole diameter</span>);
  }

  if (parts.length === 0) return null;

  return (
    <p
      data-testid="selection-summary"
      className={`font-sandvik-text text-sm text-n6 ${className}`.trim()}
    >
      You have selected: {parts}
    </p>
  );
}

export { SelectionSummary };
